import { Document, Font, Page, pdf, Text, View } from '@react-pdf/renderer';
import { saveAs } from 'file-saver';
import i18next from 'i18next';
import styles from '../../constants/soaPdfStyles';
import formatDate from '../../utils/dateHelpers';

Font.register({
  family: 'Work Sans',
  src: 'https://fonts.gstatic.com/s/worksans/v2/ElUAY9q6T0Ayx4zWzW63VKCWcynf_cDxXwCLxiixG1c.ttf',
});

Font.register({
  family: 'Lato',
  src: 'https://fonts.gstatic.com/s/lato/v13/v0SdcGFAl2aezM9Vq_aFTQ.ttf',
});

function createGroups(groups) {
  const mainGroups = [];
  const subGroups = [];

  groups.forEach((group) => {
    if (group.startsWith('ISO')) {
      return; // Skip this group
    }

    const splitGroup = group.split('.');
    if (splitGroup.length === 3) {
      subGroups.push(group);
    } else {
      mainGroups.push(group);
    }
  });

  return { mainGroups, subGroups };
}

function compareControlTitles(a, b) {
  const aTitle = a.title.split(':')[0];
  const bTitle = b.title.split(':')[0];

  const aParts = aTitle.split('.').map(Number);
  const bParts = bTitle.split('.').map(Number);

  for (let i = 0; i < aParts.length && i < bParts.length; i++) {
    if (aParts[i] < bParts[i]) {
      return -1;
    }
    if (aParts[i] > bParts[i]) {
      return 1;
    }
  }
  return aParts.length - bParts.length;
}

function prepareData(groups, controls) {
  const { mainGroups, subGroups } = createGroups(groups);
  const preparedData = mainGroups.map((mainGroup) => {
    const mainGroupControls = controls.filter(
      (control) =>
        control.title.split(':')[0].startsWith(mainGroup.split(':')[0]) &&
        !subGroups.some((subGroup) => control.title.split(':')[0].startsWith(subGroup.split(':')[0])),
    );

    const mainGroupRelatedSubGroups = /^[A-Za-z]/.test(mainGroup)
      ? subGroups.filter((subGroup) => subGroup.split(':')[0].startsWith(mainGroup.split(':')[0]))
      : [];

    const preparedSubGroups = mainGroupRelatedSubGroups.map((subGroup) => {
      const subGroupControls = controls.filter((control) =>
        control.title.split(':')[0].startsWith(subGroup.split(':')[0]),
      );
      return { title: subGroup, controls: subGroupControls };
    });

    return { title: mainGroup, controls: mainGroupControls, subGroups: preparedSubGroups };
  });

  return preparedData;
}

function RenderReportTitle({ soaInfo, frameworkTitle, organizationName }) {
  const date = formatDate(soaInfo.created);
  const { version, unfinishedControls } = soaInfo;

  const validity = (() => {
    const labelKey = unfinishedControls === 1 ? 'controlIsIncomplete' : 'controlsAreIncomplete';
    return ` ${i18next.t('framework.soaPdf.notValid').toUpperCase()} (${unfinishedControls} ${i18next.t(`framework.soaPdf.${labelKey}`)})`;
  })();

  return (
    <View wrap={false}>
      <Text style={styles.pageTitle}>{frameworkTitle}</Text>
      <Text style={styles.pageSubTitle}>{i18next.t('framework.soaPdf.soa')}</Text>
      <View style={styles.infoBoxRow}>
        <View style={styles.infoBox}>
          <Text style={styles.boxTitle}>{i18next.t('framework.organization')}</Text>
          <Text style={styles.boxSubTitle}>{organizationName}</Text>
        </View>
        <View style={styles.infoBox}>
          <Text style={styles.boxTitle}>
            <Text style={styles.boxTitle}>{`${i18next.t('framework.soaPdf.version')} ${version} `}</Text>
            {unfinishedControls > 0 && (<Text style={styles.mainOrange}>{validity}</Text>)}
          </Text>
          <Text style={styles.boxSubTitle}>{`${i18next.t('framework.soaPdf.created')} ${date}`}</Text>
        </View>
      </View>
      <View style={styles.quickTagContainer}>
        <Text style={styles.boxTitle}>{i18next.t('framework.soaPdf.quickTag')}</Text>
        <Text style={styles.boxSubTitle}>{i18next.t('framework.soaPdf.quickTagDesc')}</Text>
      </View>
    </View>
  );
}

function RenderControlHeaders({ isUnfinished }) {
  const textColor = isUnfinished ? '#EF6C00' : '#2EAF98';

  return (
    <View fixed style={styles.controlHeaderRow}>
      <Text style={[styles.subheaderText, styles.wideColumn, { color: textColor }]}>
        {i18next.t('framework.soaPdf.controls')}
      </Text>
      <Text style={[styles.subheaderText, styles.smallColumn, { color: textColor }]}>
        {i18next.t('framework.soaPdf.applicability')}
      </Text>
      <Text style={[styles.subheaderText, styles.mediumColumn, { color: textColor }]}>
        {i18next.t('framework.soaPdf.justification')}
      </Text>
      <Text style={[styles.subheaderText, styles.mediumColumn, { color: textColor }]}>
        {i18next.t('framework.soaPdf.controlOwner')}
      </Text>
    </View>
  );
}

function ControlRow({ control }) {
  const justificationTranslateKeys = {
    nri: 'control.evaluation.inApplicability.nriDesc',
    nro: 'control.evaluation.inApplicability.nroDesc',
    nrr: 'control.evaluation.inApplicability.nrrDesc',
    otp: 'control.evaluation.inApplicability.otpDesc',
  };

  const translations = control.applicabilityReasons.map((reason) => {
    const translationKey = justificationTranslateKeys[reason];
    return i18next.t(translationKey);
  });

  const rowText = control.applicable
    ? control.applicabilityReasons.map((reason) => reason.toUpperCase()).join(', ')
    : translations.join(', ');

  return (
    <View wrap={false} key={control.title} style={control.applicable ? {} : styles.notApplicableBackground}>
      <View style={styles.controlTextContainer}>
        <View style={styles.wideColumn}>
          <Text style={[styles.controlText]}>{control.title}</Text>
        </View>
        <View style={styles.smallColumn}>
          <Text style={[styles.controlText, control.applicable ? styles.mainGreen : styles.mainRed]}>
            {control.applicable
              ? i18next.t('framework.soaPdf.applicable')
              : i18next.t('framework.soaPdf.notApplicable')}
          </Text>
        </View>
        <View style={styles.mediumColumn}>
          <Text style={[styles.rowText]}>{rowText}</Text>
        </View>
        <Text style={[styles.rowText, styles.mediumColumn]}>
          {control.owner.length > 0 ? control.owner.join(', ') : ''}
        </Text>
      </View>
    </View>
  );
}

function UnfinishedControlRow({ control }) {
  return (
    <View wrap={false} key={control.title}>
      <View style={styles.unfinshedControlContainer}>
        <Text style={[styles.controlText, styles.wideColumn]}>{control.title}</Text>
        <Text style={[styles.controlText, styles.smallColumn, control.applicable ? styles.mainGreen : styles.mainRed]}>
          {control.applicable ? i18next.t('framework.soaPdf.applicable') : i18next.t('framework.soaPdf.notApplicable')}
        </Text>
      </View>
    </View>
  );
}

function Group({ title, controls, subGroups }) {
  const filteredControls = controls.filter((control) => control.title.split(':')[0].startsWith(title.split(':')[0]));

  return (
    <View key={title}>
      <Text style={styles.groupTitle}>{title}</Text>
      <View>
        <RenderControlHeaders isUnfinished={false} />
        {filteredControls.sort(compareControlTitles).map((control) => (
          <ControlRow control={control} />
        ))}
        {subGroups.map(({ title, controls }) => (
          <SubGroup title={title} controls={controls} />
        ))}
      </View>
    </View>
  );
}

function SubGroup({ title, controls }) {
  const filteredControls = controls.filter((control) => control.title.split(':')[0].startsWith(title.split(':')[0]));

  return (
    <View wrap={false} key={title}>
      <Text style={styles.subGroupTitle}>{title}</Text>
      {filteredControls.sort(compareControlTitles).map((control) => (
        <ControlRow control={control} />
      ))}
    </View>
  );
}

function RenderUnfinishedControls({ controls }) {
  return (
    <View>
      <View style={styles.longTextBox}>
        <Text style={styles.unfinishedControlTitle}>{i18next.t('framework.soaPdf.incompleteControls')}</Text>
        <Text style={styles.boxSubTitle}>{i18next.t('framework.soaPdf.incompleteDesc')}</Text>
      </View>
      <RenderControlHeaders isUnfinished />
      {controls.map((control) => (
        <UnfinishedControlRow key={control.id} control={control} />
      ))}
    </View>
  );
}

function PdfFile({ item, groups, soaInfo, frameworkTitle, organizationName }) {
  const { controls, unfinishedControls } = item;
  const preparedData = prepareData(groups, controls);

  return (
    <Document>
      <Page size="A4" style={styles.page} dpi={150}>
        <RenderReportTitle soaInfo={soaInfo} frameworkTitle={frameworkTitle} organizationName={organizationName} />
        {preparedData.map(({ title, controls, subGroups }) => (
          <View key={title} style={styles.groupControls}>
            <Group title={title} controls={controls} subGroups={subGroups} />
          </View>
        ))}
        <Text
          style={styles.pageNumber}
          render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`}
          fixed
        />
      </Page>
      {unfinishedControls.length > 0 && (
        <Page size="A4" style={styles.page} dpi={150}>
          <RenderUnfinishedControls controls={unfinishedControls} />
          <Text
            style={styles.pageNumber}
            render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`}
            fixed
          />
        </Page>
      )}
    </Document>
  );
}

export const downloadPdf = async ({ item, groups, soaInfo, frameworkTitle, organizationName }) => {
  const document = PdfFile({ item, groups, soaInfo, frameworkTitle, organizationName });
  const blob = await pdf(document).toBlob();
  saveAs(blob, `${frameworkTitle}.pdf`);
};

export default PdfFile;
